/**
 * Header global del módulo de soporte.
 *
 * Renderiza un dropdown de tenants en la parte superior cuando el tenant
 * actual está marcado como main support en server/config/tenants.json.
 * Permite ver tickets de cualquier tenant del registry, o "Todos" para
 * el dashboard agregado.
 */

import React, { useContext } from 'react';
import { Box, FormControl, InputLabel, Select, MenuItem, Chip, Stack, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { SoporteContext } from '../../../context/SoporteContext';

const VALOR_SESION = '__session__';
const VALOR_ALL = 'all';

const SoporteHeader = () => {
  const {
    isMainSupport,
    tenantsDisponibles,
    tenantSeleccionado,
    setTenantSeleccionado,
    sessionTenant,
    isOperandoEnOtroTenant,
    isAllMode
  } = useContext(SoporteContext);

  if (!isMainSupport) return null;
  if (!tenantsDisponibles || tenantsDisponibles.length === 0) return null;

  // Valor mostrado en el select: si tenantSeleccionado es null o coincide con sesión, mostramos sesión.
  const valorSelect = (() => {
    if (!tenantSeleccionado || tenantSeleccionado === sessionTenant) return VALOR_SESION;
    return tenantSeleccionado;
  })();

  const handleChange = (event) => {
    const v = event.target.value;
    if (v === VALOR_SESION) {
      setTenantSeleccionado(null);
    } else {
      setTenantSeleccionado(v);
    }
  };

  const sessionEntry = tenantsDisponibles.find((t) => t.tenant === sessionTenant);
  const otrosTenants = tenantsDisponibles.filter((t) => t.tenant !== sessionTenant);
  const tenantSeleccionadoEntry = tenantsDisponibles.find((t) => t.tenant === tenantSeleccionado);
  const nombreDelTarget = tenantSeleccionadoEntry?.displayName || tenantSeleccionado;

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        py: 1.5,
        px: 2,
        backgroundColor: isOperandoEnOtroTenant ? '#e8f5e9' : '#f5f5f5',
        borderBottom: '1px solid',
        borderColor: 'divider',
        gap: 2,
        flexWrap: 'wrap'
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
          Soporte de tenant:
        </Typography>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel id="soporte-tenant-select-label">Tenant</InputLabel>
          <Select
            labelId="soporte-tenant-select-label"
            value={valorSelect}
            onChange={handleChange}
            label="Tenant"
          >
            <MenuItem value={VALOR_SESION}>
              {sessionEntry ? `${sessionEntry.displayName} (mi tenant)` : `${sessionTenant || ''} (mi tenant)`}
            </MenuItem>
            <MenuItem value={VALOR_ALL}>Todos (dashboard)</MenuItem>
            {otrosTenants.map((t) => (
              <MenuItem key={t.tenant} value={t.tenant}>
                {t.displayName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {isOperandoEnOtroTenant && !isAllMode && (
          <Chip
            icon={<SwapHorizIcon />}
            label={`Operando en ${nombreDelTarget} — las acciones se aplican en esa BD`}
            color="success"
            size="small"
          />
        )}
        {isAllMode && (
          <Chip
            icon={<VisibilityIcon />}
            label="Vista global — dashboard agregado"
            color="info"
            size="small"
            variant="outlined"
          />
        )}
      </Stack>
    </Box>
  );
};

export default SoporteHeader;
